import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, Clock, AlertTriangle, Copy, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import API from '@/lib/api';

const statusConfig = {
  active: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', bgColor: 'bg-emerald-50', icon: CheckCircle, label: 'ACTIVE' },
  pending_payment: { color: 'bg-amber-50 text-amber-700 border-amber-200', bgColor: 'bg-amber-50', icon: Clock, label: 'PENDING PAYMENT' },
  expired: { color: 'bg-[#F6F8FC] text-[#475467] border-[#D0D5DD]', bgColor: 'bg-[#F6F8FC]', icon: XCircle, label: 'EXPIRED' },
  revoked: { color: 'bg-red-50 text-red-700 border-red-200', bgColor: 'bg-red-50', icon: AlertTriangle, label: 'REVOKED' },
};

export default function VerifyLicense() {
  const { licenseId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLicense = async () => {
      try {
        const res = await API.get(`/verify/${licenseId}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'License not found');
      } finally {
        setLoading(false);
      }
    };
    if (licenseId) fetchLicense();
  }, [licenseId]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Verification link copied');
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F6F8FC] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#F6F8FC] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl border border-[#D0D5DD] shadow-xl p-12 text-center max-w-md">
        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
          <XCircle className="w-8 h-8 text-red-500" />
        </div>
        <h1 className="font-heading text-2xl font-semibold text-[#101828] mb-2">License Not Found</h1>
        <p className="text-[#475467] text-sm mb-6">{error}</p>
        <Link to="/"><Button data-testid="verify-home-btn" className="btn-primary">Go Home</Button></Link>
      </div>
    </div>
  );

  const terms = data.terms || {};
  const creatorName = data.creator?.display_name || 'Unknown';
  const st = statusConfig[data.status] || statusConfig.expired;
  const StatusIcon = st.icon;

  return (
    <div className="min-h-screen bg-[#F6F8FC]">
      <nav className="bg-white border-b border-[#D0D5DD]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="font-heading text-xl font-semibold text-[#101828]">Mylikeness</Link>
          <Badge className="bg-[#EEF2FA] text-[#2563EB] border-[#D0D5DD]">
            <Shield className="w-3 h-3 mr-1" /> Public Verification
          </Badge>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12" data-testid="verify-license-page">
        <div className="bg-white rounded-2xl border border-[#D0D5DD] shadow-lg overflow-hidden">
          <div className={`p-6 flex items-center gap-4 border-b border-[#D0D5DD] ${st.bgColor}`}>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${data.status === 'active' ? 'bg-emerald-100' : 'bg-white'}`}>
              <StatusIcon className={`w-6 h-6 ${data.status === 'active' ? 'text-emerald-600' : 'text-[#475467]'}`} />
            </div>
            <div className="flex-1">
              <p className="text-xs text-[#475467] font-medium uppercase tracking-wider">License Verification</p>
              <h1 className="font-heading text-xl font-semibold text-[#101828]" data-testid="verify-license-id">{data.license_id}</h1>
            </div>
            <Badge className={`${st.color}`} data-testid="verify-license-status">{st.label}</Badge>
          </div>

          <div className="p-6 space-y-6">
            <div className="flex items-center gap-3 p-4 bg-[#F6F8FC] rounded-xl">
              <div className="w-10 h-10 rounded-full bg-[#EEF2FA] flex items-center justify-center text-[#2563EB] font-semibold">
                {creatorName[0]}
              </div>
              <div>
                <p className="text-[#475467] text-xs">Licensed Creator</p>
                <p className="text-[#101828] font-semibold" data-testid="verify-creator-name">{creatorName}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="Platforms" value={terms.platforms?.join(', ') || 'N/A'} testId="verify-platforms" />
              <InfoRow label="Territory" value={terms.territory?.join(', ') || 'N/A'} testId="verify-territory" />
              <InfoRow label="Term" value={`${data.starts_at || '?'} to ${data.ends_at || '?'}`} testId="verify-term" />
              <InfoRow label="Usage Type" value={terms.usage_type || 'N/A'} testId="verify-usage" />
              <InfoRow label="Endorsement" value={terms.endorsement_enabled ? 'Yes' : 'No'} testId="verify-endorsement" />
              <InfoRow label="AI Training" value={terms.training_allowed ? 'Permitted' : 'Not Permitted'} testId="verify-training" />
            </div>

            {terms.banned_categories?.length > 0 && (
              <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                <p className="text-xs text-red-600 font-medium mb-3 uppercase tracking-wider">Prohibited Categories</p>
                <div className="flex flex-wrap gap-2">
                  {terms.banned_categories.map((cat) => (
                    <Badge key={cat} variant="outline" className="text-red-600 border-red-200 bg-white text-xs">{cat.replace('_', ' ')}</Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-[#F6F8FC] rounded-xl p-4 border border-[#D0D5DD]">
              <p className="text-xs text-[#475467] leading-relaxed">
                No sublicensing or resale permitted. No model training rights granted. Content may contain AI-generated or digitally edited elements. This license verification is provided by Mylikeness for compliance purposes.
              </p>
            </div>

            <div className="flex gap-3">
              <Button data-testid="verify-copy-link-btn" variant="outline" onClick={copyLink} className="flex-1 h-11 border-[#D0D5DD] hover:bg-[#F6F8FC]">
                <Copy className="w-4 h-4 mr-2" /> Copy Link
              </Button>
              <Button asChild className="flex-1 h-11 btn-primary">
                <Link to="/">
                  <ExternalLink className="w-4 h-4 mr-2" /> Visit Mylikeness
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="text-center mt-8">
          <p className="text-[#475467] text-xs">
            Powered by <span className="font-medium text-[#101828]">Mylikeness</span> • Likeness management made simple
          </p>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, testId }) {
  return (
    <div className="p-3 bg-[#F6F8FC] rounded-xl">
      <p className="text-xs text-[#475467] mb-1">{label}</p>
      <p className="text-sm text-[#101828] font-medium" data-testid={testId}>{value}</p>
    </div>
  );
}
